import { ArrowDownIcon, ArrowUpIcon } from '@langchain/untitled-ui-icons';

import { RefObject, useEffect, useState } from 'react';

import { cn } from '@/utils/tailwind';

const FloatingScrollButton = ({
  containerRef,
  className,
}: {
  containerRef: RefObject<HTMLDivElement>;
  className?: string;
}) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isInTopHalf, setIsInTopHalf] = useState(false);
  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;

    const checkOverflow = () => {
      if (container) {
        const { scrollHeight, clientHeight } = container;
        setIsOverflowing(scrollHeight > clientHeight);
      }
    };

    const checkScrollPosition = () => {
      if (container) {
        const { scrollTop, scrollHeight, clientHeight } = container;

        // Calculate if we're in the top half of the scrollable area
        const scrollableDistance = scrollHeight - clientHeight;
        const isInTopHalf = scrollTop < scrollableDistance / 2;

        setIsInTopHalf(isInTopHalf);
      }
    };

    // Run on mount
    checkOverflow();
    checkScrollPosition();

    // Observe size changes
    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();
      checkScrollPosition();
    });
    resizeObserver.observe(container);

    // Observe content mutations
    const mutationObserver = new MutationObserver(() => {
      checkOverflow();
      checkScrollPosition();
    });
    mutationObserver.observe(container, {
      childList: true,
      subtree: true,
    });

    // Add scroll event listener
    container.addEventListener('scroll', checkScrollPosition);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      container.removeEventListener('scroll', checkScrollPosition);
    };
  }, [containerRef]);

  const scrollToBottom = () => {
    if (containerRef?.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  const scrollToTop = () => {
    if (containerRef?.current) {
      containerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  if (!isOverflowing) return null;

  return (
    <button
      onClick={isInTopHalf ? scrollToBottom : scrollToTop}
      type="button"
      className={cn(
        'absolute bottom-5 left-1/2 flex -translate-x-1/2 flex-row items-center gap-2 rounded-full border border-primary bg-tertiary px-2 py-1 font-medium text-secondary hover:bg-quaternary',
        className
      )}
    >
      {isInTopHalf ? (
        <ArrowDownIcon className="h-4 w-4" />
      ) : (
        <ArrowUpIcon className="h-4 w-4" />
      )}
      <span className="text-sm">
        {isInTopHalf ? 'Scroll to bottom' : 'Scroll to top'}
      </span>
    </button>
  );
};

export default FloatingScrollButton;
